import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { UntypedFormBuilder, Validators } from '@angular/forms'
import { Subscription } from 'rxjs'
import { finalize, switchMap } from 'rxjs/operators'
import { Application } from 'src/app/account/services/application.service'
import { DynamoDbBackupMode, DynamoDbTableConfig, SyncPair } from 'src/app/account/services/sync-pair.service'
import { ApiService } from 'src/app/services/api.service'
import { ResourceConfigDto } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { handleArpioError } from 'src/utils/handle-error'
import { ViewableIssue } from '../../issues-view/issues-view.component'

@Component({
  selector: 'dynamodb-backup-mode-issue',
  templateUrl: './dynamodb-backup-mode-issue.component.html',
  styleUrls: ['./dynamodb-backup-mode-issue.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DynamoDbBackupModeIssueComponent implements OnInit {
  // Exposed for the template's radio [value] bindings -- Angular templates can't import directly.
  DynamoDbBackupMode = DynamoDbBackupMode

  @Input() issue: ViewableIssue = null
  @Input('sync-pair') syncPair: SyncPair = null
  @Input('application') application: Application = null

  private loadingSubscription: Subscription = null
  private savingSubscription: Subscription = null
  private saved = false
  private dynamoDbTableConfig: DynamoDbTableConfig = null

  constructor(
    public uiService: UiService,
    public apiService: ApiService,
    private formBuilder: UntypedFormBuilder
  ) {}

  ngOnInit(): void {
    let getSyncPairResourceConfig$ = !!this.syncPair
      ? this.syncPair.getSyncPairResourceConfig$(this.issue.arn)
      : this.application.getSyncPairResourceConfig$(this.issue.arn)
    this.loadingSubscription = getSyncPairResourceConfig$
      .pipe(finalize(() => (this.loadingSubscription = null)))
      .subscribe(
        (resourceConfig) => {
          if (!!resourceConfig) {
            this.dynamoDbTableConfig = resourceConfig.resourceConfig as DynamoDbTableConfig
            if (this.dynamoDbTableConfig) {
              this.backupModeFormModel.setValue({
                backupMode: this.dynamoDbTableConfig.backupMode,
              })
            }
          }
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  backupModeFormModel = this.formBuilder.group({
    backupMode: [null, Validators.required],
  })

  saveClick() {
    this.saveConfig()
  }

  saveConfig() {
    let config = {
      resourceConfigType: DynamoDbTableConfig.resourceConfigType,
      backupMode: this.backupMode,
    } as ResourceConfigDto

    let saver$ = null
    if (this.syncPair) {
      saver$ = this.syncPair.setSyncPairResourceConfig$(this.issue.arn, config)
    } else {
      saver$ = this.application.setSyncPairResourceConfig$(this.issue.arn, config)
    }
    this.savingSubscription = saver$
      .pipe(
        switchMap((_config) => this.issue.deleter$()),
        finalize(() => (this.savingSubscription = null))
      )
      .subscribe(
        (_response) => {
          this.saved = true
          this.disableControls()
        },
        (err) => {
          handleArpioError(window.location.href, this.apiService, this.uiService)(err)
        }
      )
  }

  get backupMode() {
    return this.backupModeFormModel.value.backupMode
  }

  get saving() {
    return !!this.savingSubscription && !this.savingSubscription.closed
  }

  get saveStatus() {
    if (this.saving) {
      return 'saving'
    }
    if (this.saved) {
      return 'saved'
    }
    return 'unsaved'
  }

  disableControls() {
    this.backupModeFormModel.disable()
  }

  get validConfig() {
    return !this.backupModeFormModel.invalid
  }
}
